import { z } from "zod";

export const runtimeEnvironmentSchema = z.enum([
  "local",
  "dev",
  "qa",
  "stage",
  "prod",
]);

const text = z.string().refine((value) => !/\$\([^)]+\)/.test(value), {
  message: "Unresolved deployment variable",
});

function isOrigin(value: string, local: boolean): boolean {
  try {
    const url = new URL(value);
    const secure = url.protocol === "https:";
    const loopback = ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname);
    return (
      (secure || (local && loopback && url.protocol === "http:")) &&
      !url.username &&
      !url.password &&
      url.pathname === "/" &&
      !url.search &&
      !url.hash
    );
  } catch {
    return false;
  }
}

const httpsUrl = text.refine(
  (value) => {
    if (!value) return true;
    try {
      const url = new URL(value);
      return url.protocol === "https:" && !url.username && !url.password;
    } catch {
      return false;
    }
  },
  { message: "Expected an HTTPS URL" },
);

export const runtimeConfigSchema = z
  .object({
    schemaVersion: z.literal(1),
    environment: runtimeEnvironmentSchema,
    apiBaseUrl: text,
    apiPath: text.refine(
      (value) =>
        value === "" ||
        (value.startsWith("/") &&
          !value.startsWith("//") &&
          !/[?#\\\s]/.test(value) &&
          !value.split("/").some((part) => part === "." || part === "..")),
      { message: "Expected an API path" },
    ),
    clientId: text,
    disableAuth: z.boolean(),
    demoMode: z.boolean(),
    enableDeliveryConfig: z.boolean(),
    helpUrl: httpsUrl,
    applicationInsightsConnectionString: text,
    posthogEnabled: z.boolean(),
    posthogKey: text.transform((value) => value.trim()),
    posthogHost: httpsUrl.refine(Boolean, {
      message: "Expected a PostHog host",
    }),
  })
  .strict()
  .superRefine((config, context) => {
    const local = config.environment === "local";
    if (
      !(local && config.apiBaseUrl === "") &&
      !isOrigin(config.apiBaseUrl, local)
    ) {
      context.addIssue({
        code: "custom",
        path: ["apiBaseUrl"],
        message: "Expected an API origin",
      });
    }
    if (!config.disableAuth && !config.clientId.trim()) {
      context.addIssue({
        code: "custom",
        path: ["clientId"],
        message: "Client ID is required",
      });
    }
    if (["stage", "prod"].includes(config.environment)) {
      for (const field of ["disableAuth", "demoMode"] as const) {
        if (config[field]) {
          context.addIssue({
            code: "custom",
            path: [field],
            message: "Not permitted in this environment",
          });
        }
      }
    }
  });

export type RuntimeConfig = z.infer<typeof runtimeConfigSchema>;
export type RuntimeEnvironment = z.infer<typeof runtimeEnvironmentSchema>;

export function parseRuntimeConfig(input: unknown): Readonly<RuntimeConfig> {
  const result = runtimeConfigSchema.safeParse(input);
  if (!result.success) {
    const fields = [
      ...new Set(
        result.error.issues.map((issue) =>
          typeof issue.path[0] === "string" &&
          Object.hasOwn(runtimeConfigSchema.shape, issue.path[0])
            ? issue.path[0]
            : "document",
        ),
      ),
    ];
    throw new Error(`Invalid runtime configuration: ${fields.join(", ")}`);
  }
  return Object.freeze(result.data);
}
